import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { fetchBackgrounds, uploadBackground, deleteBackground } from '../../utils/backgroundsApi';
import { extractDominantFromUrl, getContrastiveAccent, isPresignedUrlExpired } from '../../utils/colorExtract';
import { hexToRgb } from '../../constants/colors';
import { useAuth } from '../../context/AuthContext';

const FILTER_THRESHOLD = 8;

// ─── Mini boot sequence for the upload placeholder thumbnail ─────────
const UPLOAD_LOGS = [
  { text: '> RECEIVING DATA...', color: '#9be945' },
  { text: '> VALIDATING FORMAT', color: '#34d399' },
  { text: '> PROCESSING IMAGE', color: '#34d399' },
  { text: '> S3 UPLOAD: █████', color: '#fbbf24' },
  { text: '> REGISTERING ASSET', color: '#34d399' },
  { text: '> EXTRACTING COLORS', color: '#818cf8' },
  { text: '> ANALYZING PALETTE', color: '#818cf8' },
  { text: '> ALMOST READY...', color: '#9be945', bold: true },
];

function UploadPlaceholderBoot() {
  const [logLines, setLogLines] = useState([]);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const indexRef = useRef(0);

  // Progress bar
  useEffect(() => {
    const start = Date.now();
    const tick = () => {
      const elapsed = Date.now() - start;
      setProgress(Math.min(95, (elapsed / 8000) * 95));
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, []);

  // Log lines — cycle through them
  useEffect(() => {
    const addLine = () => {
      const idx = indexRef.current % UPLOAD_LOGS.length;
      setLogLines(prev => {
        const next = [...prev, UPLOAD_LOGS[idx]];
        return next.length > 5 ? next.slice(next.length - 5) : next;
      });
      indexRef.current++;
      timerRef.current = setTimeout(addLine, 600 + Math.random() * 400);
    };
    timerRef.current = setTimeout(addLine, 300);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  return (
    <div style={{
      position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
      fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
      overflow: 'hidden', background: '#0a0a0e', borderRadius: '5px',
    }}>
      {/* Scanline */}
      <div style={{
        position: 'absolute', width: '100%', height: '20px',
        background: 'linear-gradient(0deg, transparent 0%, rgba(155,233,69,0.06) 50%, transparent 100%)',
        pointerEvents: 'none', zIndex: 10,
        animation: 'boot-scanline 3s linear infinite',
      }} />
      {/* Log lines */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        justifyContent: 'flex-end', padding: '4px 5px', gap: '1px', overflow: 'hidden',
      }}>
        {logLines.map((line, i) => (
          <div key={i} style={{
            color: line.color, fontSize: '6px', lineHeight: 1.4,
            fontWeight: line.bold ? 700 : 400, whiteSpace: 'pre',
            opacity: 0, animation: 'boot-line-appear 0.12s forwards',
            textShadow: `0 0 3px ${line.color}30`,
          }}>
            {line.text}
          </div>
        ))}
      </div>
      {/* Progress bar at bottom */}
      <div style={{ padding: '3px 5px 4px', flexShrink: 0 }}>
        <div style={{
          width: '100%', height: '2px', background: '#1a1a1a', borderRadius: '1px', overflow: 'hidden',
        }}>
          <div style={{
            height: '100%', width: `${progress}%`,
            background: 'linear-gradient(90deg, #4d7a1e, #9be945)',
            boxShadow: '0 0 4px rgba(155,233,69,0.4)',
            borderRadius: '1px', transition: 'width 0.3s linear',
          }} />
        </div>
      </div>
    </div>
  );
}

/**
 * Background picker component for selecting or uploading slide backgrounds.
 * 
 * Phase 1 (S3-Everything): All backgrounds are served via presigned S3 URLs.
 * Selection passes { url, asset_id } to the parent, which stores bgAssetId
 * in the slide data for persistence.
 * 
 * @param {Object} props
 * @param {string} props.currentBgUrl - Currently selected background URL
 * @param {string} props.currentBgAssetId - Currently selected background asset_id (S3)
 * @param {function} props.onSelect - Callback: ({ url, asset_id }) => void
 * @param {Array} [props.slides] - All slides in the current carousel (for "Current" filter)
 * @param {string} [props.activeCarouselId] - Current carousel ID (resets session uploads on switch)
 * @param {string} [props.filter] - 'current' | 'all' filter (controlled by parent)
 * @param {function} [props.onFilterChange] - Callback to change filter
 * @param {function} [props.onShowFilter] - Reports whether filter toggle should be visible
 */
export function BackgroundPicker({ currentBgUrl, currentBgAssetId, onSelect, slides = [], activeCarouselId, filter = 'current', onFilterChange, onShowFilter }) {
  const { user, onAuthChange } = useAuth();
  const [backgrounds, setBackgrounds] = useState({ presets: [], custom: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  // Map of asset_id/filename → { accent: '#hex', glow: 'rgba(...)' }
  // Derived from each custom background image's dominant color (contrastive).
  const [accentMap, setAccentMap] = useState({});
  // Background pending deletion confirmation (null = no popup)
  const [pendingDelete, setPendingDelete] = useState(null);
  // Track custom asset_ids that we already re-fetched for and confirmed missing.
  const checkedMissingRef = useRef(new Set());
  // Track which backgrounds we've already queued for color extraction
  const extractedRef = useRef(new Set());
  // Track backgrounds uploaded during this carousel session
  const sessionUploadsRef = useRef(new Set());
  const prevCarouselIdRef = useRef(activeCarouselId);
  const uploadInputRef = useRef(null);

  // ── Extract contrastive accents from custom background images ──────
  // Runs lazily after backgrounds load. Each image is analyzed once.
  // Session 62: passes asset_id for presigned URL refresh on expiry.
  // Skips backgrounds with visibly expired URLs to avoid CORS console spam.
  const extractAccentForBg = useCallback(async (bg) => {
    const key = bg.asset_id || bg.filename;
    if (!key || !bg.url || extractedRef.current.has(key)) return;
    // Skip backgrounds with expired presigned URLs that have no asset_id
    // (can't refresh without asset_id — would only cause CORS errors)
    if (isPresignedUrlExpired(bg.url) && !bg.asset_id) return;
    extractedRef.current.add(key);

    const dominant = await extractDominantFromUrl(bg.url, bg.asset_id);
    if (!dominant) return;

    const accent = getContrastiveAccent(dominant.hex);
    const rgb = hexToRgb(accent);
    const glow = rgb
      ? `rgba(${rgb.r},${rgb.g},${rgb.b},0.55)`
      : 'rgba(52,211,153,0.6)';

    setAccentMap(prev => ({ ...prev, [key]: { accent, glow } }));
  }, []);

  // Trigger extraction for all visible backgrounds (custom + presets)
  useEffect(() => {
    if (isLoading) return;
    const allBgs = [...backgrounds.custom, ...backgrounds.presets];
    if (allBgs.length === 0) return;
    // Stagger extractions slightly to avoid a burst of fetch() calls
    allBgs.forEach((bg, i) => {
      setTimeout(() => extractAccentForBg(bg), i * 80);
    });
  }, [backgrounds.custom, backgrounds.presets, isLoading, extractAccentForBg]);

  // Reset session uploads when carousel changes
  useEffect(() => {
    if (activeCarouselId !== prevCarouselIdRef.current) {
      sessionUploadsRef.current = new Set();
      prevCarouselIdRef.current = activeCarouselId;
    }
  }, [activeCarouselId]);

  // Collect all custom background asset_ids referenced by the current carousel's slides
  const carouselCustomAssetIds = useMemo(() => {
    const ids = new Set();
    slides.forEach(slide => {
      // New S3-backed backgrounds use bgAssetId (ast_ or UUID format)
      if (slide.bgAssetId && typeof slide.bgAssetId === 'string' && slide.bgAssetId.length > 8) {
        ids.add(slide.bgAssetId);
      }
      // Legacy: check backgroundUrl for custom paths
      const bg = slide.backgroundUrl;
      if (bg && bg.includes('/backgrounds/custom/')) {
        ids.add(bg); // Store URL as identifier for legacy matching
      }
    });
    return ids;
  }, [slides]);

  // ── Auto-collapse presets logic ──────────────────────────────────
  // Presets collapse when: ≥1 custom bg exists AND no preset is used by any slide.
  // User can manually override (toggle persists for session).

  const presetAssetIds = useMemo(() => {
    return new Set(backgrounds.presets.map(p => p.asset_id).filter(Boolean));
  }, [backgrounds.presets]);

  const anyPresetUsedInSlides = useMemo(() => {
    return slides.some(s => s.bgAssetId && presetAssetIds.has(s.bgAssetId));
  }, [slides, presetAssetIds]);

  // Auto-collapse condition: custom bgs exist AND no preset is used
  const shouldAutoCollapse = backgrounds.custom.length > 0 && !anyPresetUsedInSlides;

  // Manual override: null = follow auto logic, true/false = user override
  const [presetsManualOverride, setPresetsManualOverride] = useState(null);

  // Reset manual override when the auto-collapse condition flips
  // (e.g. user deletes all custom bgs → presets should expand again)
  const prevShouldAutoCollapse = useRef(shouldAutoCollapse);
  useEffect(() => {
    if (shouldAutoCollapse !== prevShouldAutoCollapse.current) {
      setPresetsManualOverride(null); // reset override on condition change
      prevShouldAutoCollapse.current = shouldAutoCollapse;
    }
  }, [shouldAutoCollapse]);

  const presetsExpanded = presetsManualOverride !== null
    ? presetsManualOverride
    : !shouldAutoCollapse;

  const togglePresets = () => setPresetsManualOverride(prev => {
    // If user has an override, flip it. Otherwise, flip from current auto state.
    if (prev !== null) return !prev;
    return shouldAutoCollapse; // currently collapsed → expand
  });

  // Determine if we should show the filter toggle
  const showFilter = backgrounds.custom.length > FILTER_THRESHOLD;

  // Report filter visibility to parent
  useEffect(() => {
    if (onShowFilter) onShowFilter(showFilter);
  }, [showFilter, onShowFilter]);

  // Compute the filtered custom backgrounds list
  const filteredCustom = useMemo(() => {
    if (!showFilter || filter === 'all') return backgrounds.custom;

    const result = backgrounds.custom.filter(bg => {
      // Match by asset_id
      if (bg.asset_id && carouselCustomAssetIds.has(bg.asset_id)) return true;
      // Legacy: match by URL fragment
      if ([...carouselCustomAssetIds].some(id => typeof id === 'string' && id.includes(bg.filename))) return true;
      // Uploaded during this carousel session
      if (sessionUploadsRef.current.has(bg.asset_id || bg.filename)) return true;
      return false;
    });

    return result;
  }, [backgrounds.custom, filter, showFilter, carouselCustomAssetIds]);

  // Load backgrounds on mount and when user identity changes
  // (e.g., anonymous → Clerk login transfers custom backgrounds to the new owner)
  useEffect(() => {
    loadBackgrounds();
  }, [user?.id]);

  // Also reload backgrounds on ANY auth state change (login, logout, merge).
  // This catches the post-merge case where user.id stays the same but
  // media_assets were transferred from the anonymous user to the Clerk user
  // (Bug 1: uploaded custom background disappears after login).
  useEffect(() => {
    if (!onAuthChange) return;
    return onAuthChange(() => {
      loadBackgrounds();
    });
  }, [onAuthChange]);

  // Re-fetch background list when current slide uses a custom bg
  // we don't know about (e.g., uploaded by another browser and received via polling).
  //
  // IMPORTANT: Never force-replace the slide's background with a preset.
  // The background may be valid but not in the picker's list (e.g., owned by
  // a different session, or the list hasn't fully loaded). Overwriting the
  // slide data would cause the "custom bg appears briefly then reverts" bug.
  // Instead, just re-fetch the list once so the picker can show it if possible.
  useEffect(() => {
    // For asset_id based backgrounds (ast_ or UUID format from migration)
    if (currentBgAssetId && typeof currentBgAssetId === 'string' && currentBgAssetId.length > 8) {
      const isPreset = backgrounds.presets.some(bg => bg.asset_id === currentBgAssetId);
      const isCustom = backgrounds.custom.some(bg => bg.asset_id === currentBgAssetId);
      if (isPreset || isCustom) return;
      if (isLoading) return;

      // Already re-fetched once and still missing — don't overwrite the slide,
      // just stop retrying. The background is still valid via useAssetHydration.
      if (checkedMissingRef.current.has(currentBgAssetId)) return;

      checkedMissingRef.current.add(currentBgAssetId);
      loadBackgrounds();
      return;
    }

    // Legacy: check by URL
    if (!currentBgUrl || !currentBgUrl.includes('/backgrounds/custom/')) return;
    const isKnown = backgrounds.custom.some(bg => currentBgUrl.includes(bg.filename));
    if (isKnown) {
      checkedMissingRef.current.delete(currentBgUrl);
      return;
    }
    if (isLoading) return;

    // Already re-fetched once and still missing — don't overwrite the slide.
    if (checkedMissingRef.current.has(currentBgUrl)) return;

    checkedMissingRef.current.add(currentBgUrl);
    loadBackgrounds();
  }, [currentBgUrl, currentBgAssetId, backgrounds.custom, backgrounds.presets, isLoading]);

  const loadBackgrounds = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await fetchBackgrounds();
      setBackgrounds(data);
    } catch (e) {
      setError('Failed to load backgrounds');
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;

    try {
      setIsUploading(true);
      setError(null);
      const result = await uploadBackground(file);
      
      // Track this upload in the session (for "Current" filter)
      sessionUploadsRef.current.add(result.asset_id || result.filename);

      // Add to custom backgrounds
      setBackgrounds(prev => ({
        ...prev,
        custom: [result, ...prev.custom],
      }));

      // Extract contrastive accent for the newly uploaded background
      extractAccentForBg(result);
      
      // Select the newly uploaded background using stable internal URL
      // (not the presigned S3 URL — backend sanitizer is the safety net,
      // but frontend should also avoid persisting presigned URLs)
      const stableUrl = result.asset_id
        ? `/api/media/asset/${result.asset_id}`
        : result.url;
      onSelect({ url: stableUrl, asset_id: result.asset_id });
    } catch (e) {
      setError(e.message || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (bg, e) => {
    e.stopPropagation();
    // Show inline confirmation instead of native window.confirm
    setPendingDelete(bg);
  };

  // Count how many slides use the given background
  const slidesUsingBg = useMemo(() => {
    if (!pendingDelete) return 0;
    const deleteId = pendingDelete.asset_id;
    if (!deleteId) return 0;
    return slides.filter(s => s.bgAssetId === deleteId).length;
  }, [pendingDelete, slides]);

  const confirmDelete = async () => {
    const bg = pendingDelete;
    if (!bg) return;
    setPendingDelete(null);

    try {
      await deleteBackground(bg.asset_id || bg.filename);

      // If the deleted background is the one currently on the slide, fall back to first preset
      const wasSelected = isSelected(bg);

      setBackgrounds(prev => ({
        ...prev,
        custom: prev.custom.filter(b => (b.asset_id || b.filename) !== (bg.asset_id || bg.filename)),
      }));

      // Remove from session uploads
      sessionUploadsRef.current.delete(bg.asset_id || bg.filename);

      if (wasSelected && backgrounds.presets.length > 0) {
        const preset = backgrounds.presets[0];
        const stableUrl = preset.asset_id
          ? `/api/media/asset/${preset.asset_id}`
          : preset.url;
        onSelect({ url: stableUrl, asset_id: preset.asset_id });
      }
    } catch (e) {
      setError(e.message || 'Delete failed');
    }
  };

  const cancelDelete = () => {
    setPendingDelete(null);
  };

  const handleSelect = (bg) => {
    // Use stable internal URL when asset_id exists (not presigned S3 URL).
    // Hydration will resolve asset_id → presigned URL for fast rendering.
    const stableUrl = bg.asset_id
      ? `/api/media/asset/${bg.asset_id}`
      : bg.url;
    onSelect({ url: stableUrl, asset_id: bg.asset_id });
  };

  const isSelected = (bg) => {
    // Primary: match by asset_id (most reliable for S3 backgrounds)
    if (bg.asset_id && currentBgAssetId) {
      return bg.asset_id === currentBgAssetId;
    }
    // Fallback: match by URL or filename
    if (!currentBgUrl) return false;
    if (bg.url && currentBgUrl === bg.url) return true;
    if (bg.filename && currentBgUrl.includes(bg.filename)) return true;
    return false;
  };

  return (
    <div className="space-y-3">
      {/* Upload dropzone */}
      <div>
        <input
          ref={uploadInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleUpload(file);
            if (uploadInputRef.current) uploadInputRef.current.value = '';
          }}
          className="hidden"
        />
        <div
          onClick={() => !isUploading && uploadInputRef.current?.click()}
          className={`upload-dropzone${isUploading ? ' upload-dropzone-loading' : ''}`}
        >
          <div className="upload-dropzone-content">
            <svg className="upload-dropzone-icon" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <div className="upload-dropzone-text">
              {isUploading ? 'Uploading...' : 'Add Your Background'}
            </div>
            <div className="upload-dropzone-sub">JPG / PNG / WEBP</div>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="text-xs text-red-400 px-1">{error}</div>
      )}

      {/* Loading state */}
      {isLoading ? (
        <div className="text-xs text-white/50 px-1">Loading backgrounds...</div>
      ) : (
        <>
          {/* Custom backgrounds — show section if custom bgs exist OR uploading */}
          {(backgrounds.custom.length > 0 || isUploading) && (
            <div className="space-y-2 relative">
              <div className="flex items-center justify-between px-1">
                <div className="text-xs text-white/50">Custom</div>
                {showFilter && (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => onFilterChange && onFilterChange('current')}
                      className="flex items-center gap-1.5 group"
                    >
                      <span
                        className="block w-[7px] h-[7px] rounded-full transition-all"
                        style={filter === 'current' ? {
                          background: '#34d399',
                          boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.35), 0 0 6px rgba(52,211,153,0.6)',
                        } : {
                          background: '#1a1b1e',
                          boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.9)',
                        }}
                      />
                      <span className={`text-[9px] font-semibold tracking-wide transition-colors ${
                        filter === 'current' ? 'text-white/70' : 'text-white/30 group-hover:text-white/50'
                      }`} style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>
                        This Carousel
                      </span>
                    </button>
                    <button
                      onClick={() => onFilterChange && onFilterChange('all')}
                      className="flex items-center gap-1.5 group"
                    >
                      <span
                        className="block w-[7px] h-[7px] rounded-full transition-all"
                        style={filter === 'all' ? {
                          background: '#e8b84b',
                          boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.35), 0 0 6px rgba(232,184,75,0.5)',
                          animation: 'led-blink 2s ease-in-out infinite',
                        } : {
                          background: '#1a1b1e',
                          boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.9)',
                        }}
                      />
                      <span className={`text-[9px] font-semibold tracking-wide transition-colors ${
                        filter === 'all' ? 'text-white/70' : 'text-white/30 group-hover:text-white/50'
                      }`} style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>
                        All
                      </span>
                    </button>
                  </div>
                )}
              </div>
              {(filteredCustom.length > 0 || isUploading) ? (
                <div className="grid grid-cols-4 gap-2.5">
                  {/* Upload placeholder with mini boot sequence */}
                  {isUploading && (
                    <div className="flex flex-col items-center">
                      <div
                        className="bg-screen-bezel"
                        style={{
                          width: '100%',
                          boxShadow: 'inset 0 4px 12px rgba(0,0,0,0.9), inset 0 0 0 1px #000, 0 0 0 1px rgba(155,233,69,0.2), 0 0 6px rgba(155,233,69,0.1), 0 1px 1px rgba(255,255,255,0.08)',
                        }}
                      >
                        {/* Pulsing LED during upload */}
                        <div
                          className="bg-screen-led"
                          style={{
                            background: '#9be945',
                            boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.35), 0 0 6px rgba(155,233,69,0.5)',
                            animation: 'led-blink 1.5s ease-in-out infinite',
                          }}
                        />
                        {/* Mini boot sequence inside the screen */}
                        <div className="bg-screen-inner">
                          <UploadPlaceholderBoot />
                        </div>
                      </div>
                    </div>
                  )}
                  {filteredCustom.map((bg) => {
                    const active = isSelected(bg);
                    const bgKey = bg.asset_id || bg.filename;
                    const accent = accentMap[bgKey];
                    return (
                      <div key={bgKey} className="flex flex-col items-center">
                        <div
                          onClick={() => handleSelect(bg)}
                          className={`bg-screen-bezel${active ? ' bg-screen-bezel-active' : ''}`}
                          style={{
                            width: '100%',
                            // Override bezel glow with contrastive accent when active
                            ...(active && accent ? {
                              boxShadow: `inset 0 4px 12px rgba(0,0,0,0.9), inset 0 0 0 1px #000, 0 0 0 1.5px ${accent.accent}, 0 0 8px ${accent.glow}, 0 1px 1px rgba(255,255,255,0.08)`,
                            } : {}),
                          }}
                        >
                          {/* LED — contrastive accent only when active */}
                          <div
                            className={`bg-screen-led${active && !accent ? ' bg-screen-led-on' : ''}`}
                            style={active && accent ? {
                              background: accent.accent,
                              boxShadow: `inset 0 1px 2px rgba(255,255,255,0.35), 0 0 6px ${accent.glow}`,
                            } : undefined}
                          />
                          {/* Screen */}
                          <div className="bg-screen-inner">
                            <img src={bg.url} alt="" />
                          </div>
                          {/* Delete overlay */}
                          <div
                            className="bg-screen-delete"
                            onClick={(e) => handleDelete(bg, e)}
                          >
                            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round">
                              <path d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-[11px] text-white/30 px-1 py-2">
                  No custom backgrounds used in this carousel.{' '}
                  <button
                    onClick={() => onFilterChange && onFilterChange('all')}
                    className="text-white/50 hover:text-white/70 underline underline-offset-2"
                  >
                    Show all
                  </button>
                </div>
              )}

              {/* ── Delete confirmation popup (inline, like ScopeSelector pattern) ── */}
              {pendingDelete && (
                <div className="mt-2 bg-[#1a1210] border border-red-500/15 rounded-lg px-3 py-2.5 shadow-lg shadow-black/40">
                  <div className="flex items-start gap-2 text-[11px] text-red-400/85">
                    <svg className="w-3.5 h-3.5 flex-shrink-0 mt-[1px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span>
                      {slidesUsingBg > 0
                        ? `${slidesUsingBg === 1 ? '1 slide uses' : `${slidesUsingBg} slides use`} this background. Remove it?`
                        : 'Remove this custom background?'}
                    </span>
                  </div>
                  <div className="flex gap-2 mt-2.5 ml-5">
                    <button
                      onClick={confirmDelete}
                      className="px-3 py-1 text-[11px] font-medium rounded-md bg-red-500/20 text-red-300 hover:bg-red-500/30 transition-colors"
                    >
                      Remove
                    </button>
                    <button
                      onClick={cancelDelete}
                      className="px-3 py-1 text-[11px] font-medium rounded-md bg-white/[0.04] text-white/50 hover:bg-white/[0.08] hover:text-white/70 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Preset backgrounds — collapsible */}
          <div className="space-y-2">
            <button
              onClick={togglePresets}
              className="flex items-center gap-1.5 px-1 w-full text-left group"
            >
              <svg
                className={`w-2.5 h-2.5 text-white/30 transition-transform duration-200 ${presetsExpanded ? 'rotate-90' : ''}`}
                fill="currentColor" viewBox="0 0 20 20"
              >
                <path d="M6 4l8 6-8 6V4z" />
              </svg>
              <span className="text-xs text-white/50 group-hover:text-white/70 transition-colors">
                Presets ({backgrounds.presets.length})
              </span>
            </button>
            {presetsExpanded && (
              <div className="grid grid-cols-4 gap-2.5">
                {backgrounds.presets.map((bg) => {
                  const active = isSelected(bg);
                  const bgKey = bg.asset_id || bg.filename;
                  const accent = accentMap[bgKey];
                  // Derive a short label from filename: forest-01.jpg → FOREST 01
                  const label = bg.filename
                    ? bg.filename.replace(/\.[^.]+$/, '').replace(/-/g, ' ').toUpperCase()
                    : '';
                  return (
                    <div key={bgKey} className="flex flex-col items-center">
                      <div
                        onClick={() => handleSelect(bg)}
                        className={`bg-screen-bezel${active ? ' bg-screen-bezel-active' : ''}`}
                        style={{
                          width: '100%',
                          ...(active && accent ? {
                            boxShadow: `inset 0 4px 12px rgba(0,0,0,0.9), inset 0 0 0 1px #000, 0 0 0 1.5px ${accent.accent}, 0 0 8px ${accent.glow}, 0 1px 1px rgba(255,255,255,0.08)`,
                          } : {}),
                        }}
                      >
                        {/* LED — contrastive accent only when active */}
                        <div
                          className={`bg-screen-led${active && !accent ? ' bg-screen-led-on' : ''}`}
                          style={active && accent ? {
                            background: accent.accent,
                            boxShadow: `inset 0 1px 2px rgba(255,255,255,0.35), 0 0 6px ${accent.glow}`,
                          } : undefined}
                        />
                        {/* Screen */}
                        <div className="bg-screen-inner">
                          <img src={bg.url} alt="" />
                        </div>
                      </div>
                      <div className={`bg-screen-label${active ? ' bg-screen-label-active' : ''}`}>
                        {label}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
